/**
 * Messaging System Component
 *
 * Real-time messaging with automatic polling and advanced UX features.
 * Mobile-first: deep-linking via URL (?conversation=id), browser back button,
 * keyboard-aware input, filter search.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';

const MOBILE_BREAKPOINT = 768;
const POLL_ACTIVE_MS = 60000;     // open chat → fresher (1 min)
const POLL_IDLE_MS   = 300000;    // only conversation list (5 mins)

export default (initialData = {}) => ({
    conversations: [],
    messages: [],
    activeConversationId: null,
    currentUserId: initialData.currentUserId || null,
    newMessage: '',
    loadingConversations: true,
    loadingMessages: false,
    sending: false,
    refreshTimer: null,
    searchQuery: '',
    showHeaderMenu: false,
    showReportModal: false,
    reportReason: 'spam',
    reportDetails: '',
    reporting: false,

    _visibilityHandler: null,
    _popStateHandler: null,
    _resizeHandler: null,
    _viewportHandler: null,

    // Computed: Active conversation
    get activeConversation() {
        return this.conversations.find(c => String(c.id) === String(this.activeConversationId)) || null;
    },

    get filteredConversations() {
        const q = (this.searchQuery || '').trim().toLowerCase();
        if (!q) return this.conversations;
        return this.conversations.filter((conv) => {
            const haystack = [
                conv.target_name,
                conv.horse_name,
                conv.last_message_content
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
    },

    isMobile() {
        return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
    },

    async init() {
        try {
            await this.fetchConversations();

            // Deep-link from URL: ?conversation=123
            const urlParams = new URLSearchParams(window.location.search);
            const urlConv = urlParams.get('conversation');
            if (urlConv && this.conversations.some(c => String(c.id) === String(urlConv))) {
                await this.selectConversation(urlConv, { replace: true });
            } else if (!this.isMobile() && this.conversations.length > 0) {
                // Desktop only: auto-select first conversation so right panel is not empty.
                await this.selectConversation(this.conversations[0].id, { replace: true });
            }
        } finally {
            this.$nextTick(() => document.getElementById('lmk-messages-preload')?.remove());
        }

        this._schedulePoll(POLL_ACTIVE_MS);

        this._visibilityHandler = () => {
            if (document.hidden) {
                this._stopPolling();
            } else {
                this._schedulePoll(this.activeConversationId ? POLL_ACTIVE_MS : POLL_IDLE_MS);
                this.fetchConversations(true);
                if (this.activeConversationId) {
                    this.fetchMessages(this.activeConversationId, true);
                }
            }
        };
        document.addEventListener('visibilitychange', this._visibilityHandler);

        // Back/forward button → sync active conversation with URL.
        this._popStateHandler = () => {
            const params = new URLSearchParams(window.location.search);
            const next = params.get('conversation');
            if (next) {
                if (String(next) !== String(this.activeConversationId)) {
                    this._selectFromUrl(next);
                }
            } else if (this.activeConversationId) {
                this._closeWithoutHistory();
            }
        };
        window.addEventListener('popstate', this._popStateHandler);

        // Resize: handle rotation / viewport changes between mobile/desktop.
        this._resizeHandler = () => {
            if (!this.activeConversationId && !this.isMobile() && this.conversations.length > 0) {
                this.selectConversation(this.conversations[0].id, { replace: true });
            }
        };
        window.addEventListener('resize', this._resizeHandler);

        // Virtual keyboard aware scroll on iOS/Android.
        if (window.visualViewport) {
            this._viewportHandler = () => {
                const container = this.$refs && this.$refs.messagesContainer;
                if (container && this.isNearBottom()) {
                    this.$nextTick(() => this.scrollToBottom());
                }
            };
            window.visualViewport.addEventListener('resize', this._viewportHandler);
        }
    },

    _schedulePoll(intervalMs) {
        this._stopPolling();
        const tick = () => {
            if (document.hidden) return;
            this.fetchConversations(true);
            if (this.activeConversationId) {
                this.fetchMessages(this.activeConversationId, true);
            }
        };
        this.refreshTimer = setInterval(tick, intervalMs);
    },

    _stopPolling() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    },

    _updateUrl(convId, { replace = false } = {}) {
        try {
            const url = new URL(window.location.href);
            if (convId) {
                url.searchParams.set('conversation', String(convId));
            } else {
                url.searchParams.delete('conversation');
            }
            const method = replace ? 'replaceState' : 'pushState';
            window.history[method]({ conversation: convId || null }, '', url.toString());
        } catch (e) {
            // History API unavailable — silently ignore.
        }
    },

    async _selectFromUrl(id) {
        this.activeConversationId = id;
        await this.fetchMessages(id);
    },

    _closeWithoutHistory() {
        this.activeConversationId = null;
        this.messages = [];
        this._schedulePoll(POLL_IDLE_MS);
    },

    async fetchConversations(background = false) {
        if (!background) this.loadingConversations = true;

        try {
            const data = await LovasokAPI.get('conversations');
            this.conversations = data.conversations || [];
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            if (!background) this.loadingConversations = false;
        }
    },

    async selectConversation(id, { replace = false } = {}) {
        if (String(id) === String(this.activeConversationId)) return;

        this.activeConversationId = id;
        this._updateUrl(id, { replace });
        this._schedulePoll(POLL_ACTIVE_MS);

        // Optimistic: mark conversation as read locally; server will confirm.
        const conv = this.conversations.find(c => String(c.id) === String(id));
        if (conv) conv.unread_count = 0;

        await this.fetchMessages(id);
    },

    // Close conversation (mobile back button OR native back).
    closeConversation() {
        // Prefer native back so the user's browser back stack is consistent.
        if (window.history.state && window.history.state.conversation) {
            window.history.back();
            return;
        }
        this._closeWithoutHistory();
        this._updateUrl(null, { replace: true });
    },

    async fetchMessages(convId, background = false) {
        if (!background) this.loadingMessages = true;

        try {
            const data = await LovasokAPI.get(`conversations/${convId}/messages`);
            const newMessages = data.messages || [];

            const wasNearBottom = this.isNearBottom();

            this.messages = newMessages;

            if (!background || wasNearBottom) {
                this.$nextTick(() => this.scrollToBottom());
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            if (!background) this.loadingMessages = false;
        }
    },

    _updateConversationPreview(convId, messageText) {
        const conv = this.conversations.find(c => String(c.id) === String(convId));
        if (!conv) return;

        conv.last_message_content = messageText;
        conv.last_message_sender_id = this.currentUserId;
        conv.last_message_at = new Date().toISOString();

        this.conversations.sort(
            (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
        );
    },

    formatLastMessagePreview(conv) {
        const text = (conv.last_message_content || '').trim();
        if (!text) return 'Nincs üzenet';

        const isMine = this.currentUserId
            && Number(conv.last_message_sender_id) === Number(this.currentUserId);
        const preview = text.length > 80 ? `${text.slice(0, 80)}…` : text;

        return isMine ? `Te: ${preview}` : preview;
    },

    async sendMessage() {
        if (!this.newMessage.trim() || !this.activeConversationId) return;

        const messageText = this.newMessage.trim();
        this.newMessage = '';
        this.sending = true;
        this._autoResizeTextarea();

        try {
            await LovasokAPI.post(`conversations/${this.activeConversationId}/messages`, {
                message: messageText
            });

            this._updateConversationPreview(this.activeConversationId, messageText);

            await Promise.all([
                this.fetchMessages(this.activeConversationId),
                this.fetchConversations(true),
            ]);

        } catch (error) {
            console.error('Error sending message:', error);
            alert(error?.message || 'Hiba történt az üzenet küldése során.');
            this.newMessage = messageText;
        } finally {
            this.sending = false;
        }
    },

    // Textarea auto-resize on input (up to 120px, then scroll).
    _autoResizeTextarea(el) {
        const textarea = el || (this.$refs && this.$refs.messageInput);
        if (!textarea) return;
        textarea.style.height = 'auto';
        const max = 120;
        textarea.style.height = Math.min(textarea.scrollHeight, max) + 'px';
    },

    onInputMessage(event) {
        this._autoResizeTextarea(event && event.target);
    },

    scrollToBottom() {
        const container = this.$refs && this.$refs.messagesContainer;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    },

    isNearBottom() {
        const container = this.$refs && this.$refs.messagesContainer;
        if (!container) return true;

        const threshold = 100;
        return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    },

    formatDate(dateStr) {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Ma';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Tegnap';
        } else {
            return new Intl.DateTimeFormat('hu-HU', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }).format(date);
        }
    },

    formatTime(dateStr) {
        if (!dateStr) return '';

        return new Intl.DateTimeFormat('hu-HU', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(dateStr));
    },

    // Short date for list: "ma 14:30" / "tegnap" / "márc. 12."
    formatDateShort(dateStr) {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return new Intl.DateTimeFormat('hu-HU', {
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Tegnap';
        }
        return new Intl.DateTimeFormat('hu-HU', {
            month: 'short',
            day: 'numeric'
        }).format(date);
    },

    formatDateHeader(dateStr) {
        if (!dateStr) return '';

        return new Intl.DateTimeFormat('hu-HU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        }).format(new Date(dateStr));
    },

    isDifferentDay(date1, date2) {
        if (!date1 || !date2) return true;

        const d1 = new Date(date1).toDateString();
        const d2 = new Date(date2).toDateString();

        return d1 !== d2;
    },

    openReportModal() {
        this.showHeaderMenu = false;
        this.reportReason = 'spam';
        this.reportDetails = '';
        this.showReportModal = true;
    },

    closeReportModal() {
        if (this.reporting) return;
        this.showReportModal = false;
    },

    async submitReport() {
        if (!this.activeConversationId || !this.reportDetails.trim() || this.reporting) return;

        this.reporting = true;

        try {
            await LovasokAPI.post(`conversations/${this.activeConversationId}/report`, {
                reason: this.reportReason,
                details: this.reportDetails.trim(),
            });

            this.showReportModal = false;
            this.reportDetails = '';
            alert('Köszönjük! A jelentésedet továbbítottuk az adminisztrátoroknak.');
        } catch (error) {
            console.error('Error reporting conversation:', error);
            const msg = error?.message || 'Hiba történt a jelentés elküldése során.';
            alert(msg);
        } finally {
            this.reporting = false;
        }
    },

    destroy() {
        this._stopPolling();
        if (this._visibilityHandler) {
            document.removeEventListener('visibilitychange', this._visibilityHandler);
        }
        if (this._popStateHandler) {
            window.removeEventListener('popstate', this._popStateHandler);
        }
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
        }
        if (this._viewportHandler && window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this._viewportHandler);
        }
    }
});
